import { useState, useRef, useEffect } from "react";
import {
  Menu,
  X,
  Package,
  Truck,
  Factory,
  Zap,
  Wrench,
  Timer,
  RotateCcw,
  RefreshCw,
  Settings,
  Trash2,
  Recycle,
  Scale,
  CreditCard,
  QrCode,
  ArrowUp,
  Layers,
} from "lucide-react";

const phases = [
  { id: "top", label: "↑", icon: ArrowUp, name: "Produkt Top" },
  { id: "phase-selector", label: "PS", icon: Layers, name: "Phase Selector" },
  { id: "a1", label: "A1", icon: Package, name: "Raw Materials" },
  { id: "a2", label: "A2", icon: Truck, name: "Transport" },
  { id: "a3", label: "A3", icon: Factory, name: "Manufacturing" },
  { id: "a4", label: "A4", icon: Truck, name: "Transport to Site" },
  { id: "a5", label: "A5", icon: Zap, name: "Installation" },
  { id: "b1", label: "B1", icon: Wrench, name: "Use" },
  { id: "b2", label: "B2", icon: Timer, name: "Maintenance" },
  { id: "b3", label: "B3", icon: RotateCcw, name: "Repair" },
  { id: "b4", label: "B4", icon: RefreshCw, name: "Replacement" },
  { id: "b5", label: "B5", icon: Settings, name: "Refurbishment" },
  { id: "c1", label: "C1", icon: Wrench, name: "End of Life" },
  { id: "c2", label: "C2", icon: Truck, name: "Transport" },
  { id: "c3", label: "C3", icon: Recycle, name: "Waste Processing" },
  { id: "c4", label: "C4", icon: Trash2, name: "Disposal" },
  { id: "d", label: "D", icon: RefreshCw, name: "Credits for Recycling" },
  { id: "mass-balance", label: "MB", icon: Scale, name: "Mass Balance" },
  { id: "lca-result", label: "LCA", icon: RefreshCw, name: "LCA Result" },
  { id: "dpp-kort", label: "DPP", icon: CreditCard, name: "DPP Kort" },
  { id: "dpp-generator", label: "QR", icon: QrCode, name: "DPP Generator" },
];

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handlePhaseClick = (phaseId) => {
    const element = document.getElementById(phaseId);
    if (element) {
      const headerOffset = 100;
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - headerOffset,
        behavior: "smooth",
      });
    }
    setIsOpen(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        left: "24px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 50,
      }}
      ref={menuRef}
    >
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            backgroundColor: "#334155",
            color: "white",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#475569")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#334155")}
        >
          <Menu size={24} />
        </button>
      ) : (
        <div
          style={{
            backgroundColor: "#334155",
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
            padding: "12px",
            border: "1px solid #475569",
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100vh - 80px)",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                color: "white",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Phases
            </span>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "transparent",
                color: "white",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#475569")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <X size={20} />
            </button>
          </div>

          {/* Phase list */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              overflowY: "auto",
              paddingRight: "4px",
              scrollbarWidth: "thin",
            }}
          >
            {phases.map((phase) => {
              const Icon = phase.icon;
              return (
                <button
                  key={phase.id}
                  onClick={() => handlePhaseClick(phase.id)}
                  title={phase.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 8px",
                    borderRadius: "8px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    width: "100%",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#475569")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#1e293b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={12} color="white" />
                  </div>
                  <span
                    style={{
                      color: "white",
                      fontSize: "12px",
                      fontWeight: 600,
                      width: "40px",
                      flexShrink: 0,
                    }}
                  >
                    {phase.label}
                  </span>
                  <span
                    style={{
                      color: "#94a3b8",
                      fontSize: "12px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {phase.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
